import React from "react";
import { Link } from "react-router-dom";
import { Linkedin, Facebook, Instagram, MapPin } from "lucide-react";
import { LEGAL_PAGES, COUNTRIES, CONTACT_CTA } from "@/data/content";
import { useSite } from "@/context/SiteContext";
import logo from "@/assets/logo.webp";

const reviewedDate = new Date().toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" });

export default function Footer() {
  const { config } = useSite();
  const locations = config?.locations || [];

  return (
    <footer data-testid="site-footer" className="bg-navy text-slate-300">
      <div className="songket-band" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <div className="mb-5 inline-block bg-white rounded-xl px-4 py-3">
              <img src={logo} alt="Global Pass Consultants" className="h-9 w-auto" />
            </div>
            <p className="text-sm leading-relaxed text-slate-400">
              Malaysia-headquartered international business setup, expansion and business-mobility consulting. From Labuan company formation to global expansion. {CONTACT_CTA}
            </p>
            <span className="inline-block mt-4 text-xs font-semibold text-gold border border-gold/30 rounded-full px-3 py-1">Malaysia Headquarters</span>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4 text-sm">Services</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/labuan-company-setup" className="hover:text-gold transition-colors">Labuan Company Setup</Link></li>
              <li><Link to="/malaysia-business-services" className="hover:text-gold transition-colors">Malaysia Business Services</Link></li>
              <li><Link to="/global-business-expansion" className="hover:text-gold transition-colors">Global Business Expansion</Link></li>
              <li><Link to="/business-investment-migration" className="hover:text-gold transition-colors">Business & Investment Migration</Link></li>
              <li><Link to="/insights" className="hover:text-gold transition-colors">Insights</Link></li>
              <li><Link to="/contact" className="hover:text-gold transition-colors">Contact</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4 text-sm">Countries</h4>
            <ul className="space-y-2 text-sm">
              {COUNTRIES.map((c) => (
                <li key={c.slug}><Link to={`/countries/${c.slug}`} className="hover:text-gold transition-colors">{c.flag} {c.name}</Link></li>
              ))}
            </ul>
            <h4 className="text-white font-semibold mt-6 mb-3 text-sm">Legal</h4>
            <ul className="space-y-2 text-sm grid grid-cols-1">
              {LEGAL_PAGES.map((p) => (
                <li key={p.slug}><Link to={`/legal/${p.slug}`} className="hover:text-gold transition-colors">{p.title}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4 text-sm">Verified Locations</h4>
            <ul className="space-y-3 text-sm">
              {locations.map((loc) => (
                <li key={loc.key} className="flex gap-2">
                  <MapPin size={16} className="text-gold shrink-0 mt-0.5" />
                  <div>
                    <div className="text-white font-medium">{loc.country} <span className="text-slate-500 text-xs">— {loc.label}</span></div>
                    {(loc.address || loc.phone) && <div className="text-slate-400 text-xs">{loc.address}{loc.address && loc.phone ? " · " : ""}{loc.phone || ""}</div>}
                  </div>
                </li>
              ))}
            </ul>
            <div className="flex gap-3 mt-6">
              <a href={config?.social?.linkedin || "#"} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-navy-surface flex items-center justify-center hover:bg-gold hover:text-navy transition-colors"><Linkedin size={16} /></a>
              <a href={config?.social?.facebook || "#"} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-navy-surface flex items-center justify-center hover:bg-gold hover:text-navy transition-colors"><Facebook size={16} /></a>
              <a href={config?.social?.instagram || "#"} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-navy-surface flex items-center justify-center hover:bg-gold hover:text-navy transition-colors"><Instagram size={16} /></a>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10">
          <p className="text-xs text-slate-400 leading-relaxed max-w-4xl">
            Global Pass Consultants provides business consulting, coordination and market-entry support. We are not a government authority, bank, law firm, tax practice or immigration regulator. Regulated legal, tax, trust company and immigration services are provided by or referred to appropriately licensed professionals. No outcome is guaranteed.{" "}
            <Link to="/legal/professional-disclaimer" className="text-gold hover:underline">Read the full disclaimer.</Link>
          </p>
          <div className="mt-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div>
              <p className="text-xs text-slate-500">© {new Date().getFullYear()} Global Pass Consultants. All rights reserved. Information last reviewed: {reviewedDate}</p>
              <p className="text-xs text-slate-500 mt-1">Powered by <span className="text-gold font-semibold">CreADDive ADDiction, Canada</span></p>
            </div>
            <Link to="/contact" className="text-sm font-semibold text-gold hover:underline">{CONTACT_CTA}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
